import "react-toastify/dist/ReactToastify.css";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Container, Row, Col, Card, CardBody, Button, Input, Spinner,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import {
    FiRefreshCw, FiPlus, FiSearch,
} from "react-icons/fi";
import {
    DashboardPageHeader, StatTile,
} from "../../Components/Common/DashboardKit";
import SupportTicketList from "./SupportTicketList";
import SupportTicketDetailDrawer from "./SupportTicketDetailDrawer";
import SupportTicketForm from "./SupportTicketForm";
import {
    getAccounts, getMeta, getAccountMeta, getCounts, listTickets, searchTickets,
    createTicket, getGroups, getAgents, parseSupportError,
} from "./supportService";
import { FALLBACK_STATUSES, FALLBACK_PRIORITIES } from "./supportConstants";

/**
 * Support workspace — Freshdesk tickets for SS White Technologies and Shukla
 * Medical. Single page; the route + APPS tile point here. Server-side
 * authorization is the real gate (this page is defense-in-depth via
 * RoleProtected requireModule="support").
 */
const SupportDashboard = () => {
    document.title = "Support | SSW Technologies";

    // ── reference data ──────────────────────────────────────────────
    const [accounts, setAccounts] = useState([]);
    const [accountKey, setAccountKey] = useState(null);
    const [meta, setMeta] = useState(null); // { statuses, priorities, allTicketAccess }
    const [groups, setGroups] = useState([]);
    const [agents, setAgents] = useState([]);

    // ── list state ──────────────────────────────────────────────────
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(null);
    const [statusCounts, setStatusCounts] = useState(null); // account-wide per-status counts

    // ── filters ─────────────────────────────────────────────────────
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [groupFilter, setGroupFilter] = useState("");
    const [agentFilter, setAgentFilter] = useState("");

    // ── modals / drawer ─────────────────────────────────────────────
    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [openTicketId, setOpenTicketId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const statuses = meta?.statuses?.length ? meta.statuses : FALLBACK_STATUSES;
    const priorities = meta?.priorities?.length ? meta.priorities : FALLBACK_PRIORITIES;
    const allTicketAccess = !!meta?.allTicketAccess;

    const notify = useCallback((type, message) => {
        if (type === "success") toast.success(message);
        else if (type === "error") toast.error(message);
        else toast.info(message);
    }, []);

    // ── bootstrap: accounts + meta ──────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const [accs, m] = await Promise.all([getAccounts(), getMeta()]);
                setAccounts(accs);
                setMeta(m);
                const firstConfigured = accs.find((a) => a.configured) || accs[0];
                if (firstConfigured) setAccountKey(firstConfigured.key);
            } catch (err) {
                const { message } = parseSupportError(err);
                notify("error", message);
            }
        })();
    }, [notify]);

    // ── load lookups + real field choices when account changes ──────
    useEffect(() => {
        if (!accountKey) return;
        (async () => {
            try {
                const [g, a, m] = await Promise.all([
                    getGroups(accountKey),
                    getAgents(accountKey),
                    getAccountMeta(accountKey),
                ]);
                setGroups(g);
                setAgents(a);
                if (m) setMeta(m); // real status/priority labels for this account
            } catch {
                // Non-fatal — filters just have fewer options.
                setGroups([]);
                setAgents([]);
            }
            // Account-wide status counts for the tiles (best-effort; tiles fall
            // back to per-page counts if this fails).
            try {
                const c = await getCounts(accountKey);
                setStatusCounts(c);
            } catch {
                setStatusCounts(null);
            }
        })();
    }, [accountKey]);

    // ── ticket fetch (page-replace; numbered pagination) ────────────
    const fetchPage = useCallback(
        async (targetPage) => {
            if (!accountKey) return;
            setLoading(true);
            setError(null);
            try {
                let result;
                if (search.trim()) {
                    result = await searchTickets(accountKey, search.trim(), targetPage);
                } else {
                    result = await listTickets(accountKey, {
                        page: targetPage,
                        status: statusFilter || undefined,
                        priority: priorityFilter || undefined,
                        groupId: groupFilter || undefined,
                        agentId: agentFilter || undefined,
                    });
                }
                const items = result?.items || [];
                setHasMore(!!result?.hasMore);
                setTotal(typeof result?.total === "number" ? result.total : null);
                setPage(targetPage);
                setTickets(items);
            } catch (err) {
                const { message } = parseSupportError(err);
                setError(message);
                setTickets([]);
                setHasMore(false);
                setTotal(null);
            } finally {
                setLoading(false);
            }
        },
        [accountKey, search, statusFilter, priorityFilter, groupFilter, agentFilter]
    );

    // Reload page 1 whenever account or non-search filters change.
    useEffect(() => {
        if (accountKey) fetchPage(1);
    }, [accountKey, statusFilter, priorityFilter, groupFilter, agentFilter]);

    const runSearch = () => fetchPage(1);
    const refresh = () => {
        fetchPage(page);
        if (accountKey) getCounts(accountKey).then(setStatusCounts).catch(() => {});
    };
    const goPrev = () => { if (page > 1) fetchPage(page - 1); };
    const goNext = () => { if (hasMore) fetchPage(page + 1); };

    // ── summary tiles ───────────────────────────────────────────────
    // Prefer account-wide counts from the backend (/counts). Fall back to
    // counting the current page if that call wasn't available.
    const pageCounts = useMemo(() => {
        const c = { Open: 0, Pending: 0, Resolved: 0, Closed: 0 };
        tickets.forEach((t) => {
            if (c[t.statusLabel] !== undefined) c[t.statusLabel] += 1;
        });
        return c;
    }, [tickets]);

    const counts = statusCounts || pageCounts;
    const countsAreAccountWide = !!statusCounts;
    const tileSuffix = countsAreAccountWide ? "" : " (this page)";
    const tileLoading = countsAreAccountWide ? false : loading;

    // ── create ──────────────────────────────────────────────────────
    const handleCreate = async (payload) => {
        setCreating(true);
        try {
            const created = await createTicket(accountKey, payload);
            notify("success", `Ticket #${created.id} created.`);
            setCreateOpen(false);
            await fetchPage(1);
            // Open the new ticket for immediate follow-up.
            setOpenTicketId(created.id);
            setDrawerOpen(true);
        } catch (err) {
            const { message } = parseSupportError(err);
            notify("error", message);
        } finally {
            setCreating(false);
        }
    };

    const openTicket = (t) => {
        setOpenTicketId(t.id);
        setDrawerOpen(true);
    };

    const currentAccountLabel =
        accounts.find((a) => a.key === accountKey)?.label || "";

    return (
        <div className="page-content">
            <ToastContainer position="top-right" autoClose={4000} newestOnTop />
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-customer-service-2-line"
                    eyebrow="Support"
                    title="Support"
                    subtitle="Freshdesk support tickets for SS White Technologies and Shukla Medical"
                    breadcrumb={[{ label: "Support" }]}
                    actions={
                        <Button color="light" onClick={refresh}>
                            <FiRefreshCw className="me-1" /> Refresh
                        </Button>
                    }
                />

                {/* Account switcher + create */}
                <Card className="mb-3">
                    <CardBody>
                        <Row className="g-2 align-items-end">
                            <Col md={4}>
                                <label className="form-label small text-muted">Account</label>
                                <Input
                                    type="select"
                                    value={accountKey || ""}
                                    onChange={(e) => setAccountKey(e.target.value)}
                                >
                                    {accounts.map((a) => (
                                        <option key={a.key} value={a.key} disabled={!a.configured}>
                                            {a.label}{a.configured ? "" : " (not configured)"}
                                        </option>
                                    ))}
                                </Input>
                            </Col>
                            <Col md={8} className="text-md-end">
                                <Button color="primary" onClick={() => setCreateOpen(true)} disabled={!accountKey}>
                                    <FiPlus className="me-1" /> Create ticket
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Summary tiles */}
                <Row className="g-3 mb-3">
                    <Col sm={6} xl={3}><StatTile icon="ri-folder-open-line" label={`Open${tileSuffix}`} value={counts.Open || 0} tone="primary" loading={tileLoading} /></Col>
                    <Col sm={6} xl={3}><StatTile icon="ri-time-line" label={`Pending${tileSuffix}`} value={counts.Pending || 0} tone="warning" loading={tileLoading} /></Col>
                    <Col sm={6} xl={3}><StatTile icon="ri-check-line" label={`Resolved${tileSuffix}`} value={counts.Resolved || 0} tone="success" loading={tileLoading} /></Col>
                    <Col sm={6} xl={3}><StatTile icon="ri-lock-line" label={`Closed${tileSuffix}`} value={counts.Closed || 0} tone="secondary" loading={tileLoading} /></Col>
                </Row>

                {/* Filters + search */}
                <Card className="mb-3">
                    <CardBody>
                        <Row className="g-2 align-items-end">
                            <Col md={3}>
                                <label className="form-label small text-muted">Search</label>
                                <div className="d-flex gap-2">
                                    <Input
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && runSearch()}
                                        placeholder="Search by subject…"
                                    />
                                    <Button color="secondary" onClick={runSearch}>
                                        <FiSearch />
                                    </Button>
                                </div>
                            </Col>
                            <Col md={2}>
                                <label className="form-label small text-muted">Status</label>
                                <Input type="select" value={statusFilter}
                                    onChange={(e) => { setSearch(""); setStatusFilter(e.target.value); }}>
                                    <option value="">All</option>
                                    {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </Input>
                            </Col>
                            <Col md={2}>
                                <label className="form-label small text-muted">Priority</label>
                                <Input type="select" value={priorityFilter}
                                    onChange={(e) => { setSearch(""); setPriorityFilter(e.target.value); }}>
                                    <option value="">All</option>
                                    {priorities.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </Input>
                            </Col>
                            <Col md={2}>
                                <label className="form-label small text-muted">Group</label>
                                <Input type="select" value={groupFilter}
                                    onChange={(e) => { setSearch(""); setGroupFilter(e.target.value); }}>
                                    <option value="">All</option>
                                    {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </Input>
                            </Col>
                            <Col md={2}>
                                <label className="form-label small text-muted">Agent</label>
                                <Input type="select" value={agentFilter}
                                    onChange={(e) => { setSearch(""); setAgentFilter(e.target.value); }}>
                                    <option value="">All</option>
                                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </Input>
                            </Col>
                            <Col md={1} className="text-md-end">
                                <label className="form-label small text-muted d-block">&nbsp;</label>
                                <Button color="light" className="w-100"
                                    onClick={() => { setSearch(""); setStatusFilter(""); setPriorityFilter(""); setGroupFilter(""); setAgentFilter(""); }}>
                                    Clear
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* List */}
                <Card>
                    <CardBody>
                        <SupportTicketList
                            tickets={tickets}
                            loading={loading}
                            error={error}
                            page={page}
                            hasMore={hasMore}
                            total={total}
                            onOpen={openTicket}
                            onPrev={goPrev}
                            onNext={goNext}
                        />
                    </CardBody>
                </Card>
            </Container>

            {/* Create modal */}
            <SupportTicketForm
                isOpen={createOpen}
                toggle={() => setCreateOpen((v) => !v)}
                onSubmit={handleCreate}
                submitting={creating}
                priorities={priorities}
                groups={groups}
                allTicketAccess={allTicketAccess}
                accountLabel={currentAccountLabel}
            />

            {/* Detail drawer */}
            <SupportTicketDetailDrawer
                accountKey={accountKey}
                ticketId={openTicketId}
                isOpen={drawerOpen}
                toggle={() => setDrawerOpen((v) => !v)}
                onChanged={() => fetchPage(page)}
                notify={notify}
                statuses={statuses}
                priorities={priorities}
                groups={groups}
                agents={agents}
                canManage={allTicketAccess}
            />
        </div>
    );
};

export default SupportDashboard;
